import React, { useState } from "react";
import {
  EuiAvatar,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHeaderSectionItemButton,
  EuiIcon,
  EuiLink,
  EuiPopover,
  EuiSpacer,
  EuiText,
  htmlIdGenerator,
} from "@elastic/eui";
import gravatar from "gravatar";
import { useAnketaI18n } from "../../../../internationalization";
import { useAnketaAuth } from "../../../../auth/context";
import { AnketaApiToken } from "src/auth";
import { HomepageLangSwitch } from "src/homepage/lang-switch";
import { useToast } from "src/notifications";

/**
 * Local development only: the page is served from localhost. Gates the
 * "Copy JWT" action, which hands the developer their Keycloak access token to
 * replay API calls (curl/Postman) and check which routes the account reaches.
 * Never shown on a deployed origin.
 */
const isLocalhostOrigin = (): boolean => {
  const host = window.location.hostname;
  return (
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host === "127.0.0.1" ||
    host === "[::1]" ||
    host === "::1"
  );
};

export const Avatar = ({
  additionalComponent,
}: {
  additionalComponent?: JSX.Element;
}): JSX.Element => {
  const { user, keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();

  const id = htmlIdGenerator()();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loggingIn, setLoggingIn] = useState<boolean>(false);

  // Refresh first so the copied token has its full lifetime left, not the
  // last seconds before expiry.
  const copyJwt = async () => {
    try {
      await keycloak?.updateToken(60);
      if (!keycloak?.token) throw new Error("no token");
      await navigator.clipboard.writeText(keycloak.token);
      addToast({
        title: translate("anketa.header.avatar.copyJwt.success", {
          defaultMessage: "JWT copied to the clipboard",
        }),
        color: "success",
        text: translate("anketa.header.avatar.copyJwt.successText", {
          defaultMessage:
            'Use it as "Authorization: Bearer <token>" to test routes. Valid until the token expires.',
        }),
      });
    } catch {
      addToast({
        title: translate("anketa.header.avatar.copyJwt.failed", {
          defaultMessage: "Could not copy the JWT",
        }),
        color: "danger",
      });
    }
  };

  const onMenuButtonClick = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const AvatarCircle = () => {
    if (user) {
      return (
        <EuiAvatar
          name={user?.email && user?.email ? user?.email : ""}
          size="m"
          imageUrl={gravatar.url(
            user?.email && user?.email ? user?.email : "",
            {
              s: "32",
              format: "404",
              r: "pg",
            }
          )}
        />
      );
    } else {
      return <EuiAvatar name="Login" size="m" iconType="user" />;
    }
  };

  const button = (
    <EuiHeaderSectionItemButton
      aria-controls={id}
      aria-expanded={isOpen}
      aria-haspopup="true"
      aria-label={translate("anketa.header.avatar")}
      onClick={onMenuButtonClick}
    >
      <AvatarCircle />
    </EuiHeaderSectionItemButton>
  );

  return (
    <EuiPopover
      id={id}
      button={button}
      isOpen={isOpen}
      anchorPosition="downRight"
      closePopover={closeMenu}
      panelPaddingSize="none"
    >
      {/*
        Fixed 320px is fine on desktop but must never exceed a phone viewport, and
        `responsive={false}` on every EuiFlexGroup below is deliberate: EUI's
        responsive media query flips groups to column + `flex-wrap: wrap` on small
        screens, which made the column groups wrap into side-by-side columns and
        pushed the login/logout controls off-screen. We keep the intended layout at
        all widths instead.
      */}
      <div style={{ width: "min(320px, calc(100vw - 32px))" }}>
        <EuiFlexGroup
          gutterSize="m"
          className="EuiHeaderProfile"
          responsive={false}
          alignItems="flexStart"
          style={{
            padding: "15px",
          }}
        >
          <EuiFlexItem grow={false}>
            <AvatarCircle />
          </EuiFlexItem>

          {user ? (
            <EuiFlexItem>
              <EuiText>
                <h3>
                  {user?.firstName} {user?.lastName}
                </h3>
              </EuiText>
              <EuiText>
                <p>{user?.email}</p>
              </EuiText>
              <EuiSpacer size="s" />
              <EuiFlexItem grow={false}>
                <HomepageLangSwitch
                  variant="surface"
                  style={{ alignSelf: "flex-start" }}
                />
              </EuiFlexItem>
              <EuiSpacer size="s" />

              <EuiFlexItem>
                <AnketaApiToken />
              </EuiFlexItem>
              <EuiSpacer size="m" />
              <EuiFlexItem>
                <EuiFlexGroup responsive={false} justifyContent="spaceBetween">
                  <EuiFlexItem grow={false}>
                    <EuiLink
                      onClick={() => {
                        keycloak?.logout();
                      }}
                    >
                      {translate("anketa.log.out")}
                    </EuiLink>
                  </EuiFlexItem>
                  {isLocalhostOrigin() && (
                    <EuiFlexItem grow={false}>
                      <EuiLink onClick={copyJwt}>
                        <EuiIcon type="copyClipboard" size="s" />{" "}
                        {translate("anketa.header.avatar.copyJwt", {
                          defaultMessage: "Copy JWT (localhost)",
                        })}
                      </EuiLink>
                    </EuiFlexItem>
                  )}
                </EuiFlexGroup>
              </EuiFlexItem>
            </EuiFlexItem>
          ) : (
            <EuiFlexItem>
              <EuiFlexGroup responsive={false} direction="column">
                <EuiFlexItem grow={false}>
                  <HomepageLangSwitch
                    variant="surface"
                    style={{ alignSelf: "flex-start" }}
                  />
                </EuiFlexItem>
                <EuiSpacer size="s" />
                <EuiFlexItem>
                  <EuiButton
                    fill
                    fullWidth
                    color="primary"
                    iconType="user"
                    isLoading={loggingIn}
                    onClick={async () => {
                      if (keycloak) {
                        setLoggingIn(true);
                        try {
                          await keycloak.login();
                        } finally {
                          setLoggingIn(false);
                        }
                      }
                    }}
                  >
                    {translate("anketa.login.button", {
                      defaultMessage: "Login",
                    })}
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          )}
          {additionalComponent && additionalComponent}
        </EuiFlexGroup>
      </div>
    </EuiPopover>
  );
};
